import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../../Dashboard.scss';
import UserAvatar from '../../Elements/UserAvatar';

export interface TeamUser {
  id: number;
  firstName: string;
  lastName: string;
  position: string | null;
  avatar: string;
  roleId: number;
  isProjectLead: boolean;
}

interface TeamAvatarsProps {
  users: TeamUser[];
}

const AvatarGroup: React.FC<TeamAvatarsProps> = ({ users }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const plusButtonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const maxAvatars = 5;
  const displayUsers = users?.slice(0, maxAvatars);
  const remainingUsers = users?.slice(maxAvatars);
  const remaining = users?.length - maxAvatars;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Dropdown positioning constants
  const DROPDOWN_WIDTH = 240;
  const DROPDOWN_HEIGHT = 180;
  const DROPDOWN_MARGIN = 6;
  const DROPDOWN_OFFSET = 6;

  useEffect(() => {
    if (showDropdown && plusButtonRef.current) {
      const rect = plusButtonRef.current.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + DROPDOWN_OFFSET;
      // Clamp right edge
      if (left + DROPDOWN_WIDTH > window.innerWidth) {
        left = window.innerWidth - DROPDOWN_WIDTH - DROPDOWN_MARGIN;
      }
      // Clamp bottom edge
      if (top + DROPDOWN_HEIGHT > window.innerHeight) {
        top = window.innerHeight - DROPDOWN_HEIGHT - DROPDOWN_MARGIN;
      }
      setDropdownPosition({ top, left });
    }
  }, [showDropdown]);

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-2 tw-relative tw-z-50">
      <div
        className="tw-flex tw-items-center tw-justify-end tw-relative"
        style={{ flex: '0 0 auto' }}
      >
        {displayUsers?.map((user, idx) => (
          <React.Fragment key={`teamAvatar_${user.id}_${idx}`}>
            <div className={idx === 0 ? '' : 'tw--ml-3'} style={{ zIndex: 10 + idx }}>
              <UserAvatar
                id={`teamAvatar_${user.id}_${idx}`}
                avatar={user.avatar}
                name={`${user.firstName || ''} ${user.lastName || ''}`}
                position={user.position || ''}
              />
            </div>
            {!showDropdown && (
              <Tooltip
                id={`assignee_avatar_tooltip_teamAvatar_${user.id}_${idx}`}
                place="bottom"
                effect="solid"
                backgroundColor="white"
              >
                {`${user.firstName || ''} ${user.lastName || ''}`}
              </Tooltip>
            )}
          </React.Fragment>
        ))}
        {remaining > 0 && (
          <div
            className={displayUsers.length > 0 ? 'tw--ml-3' : ''}
            style={{ zIndex: 10 + displayUsers.length }}
          >
            <div
              ref={plusButtonRef}
              className="tw-flex tw-text-[12px] tw-items-center tw-justify-center tw-bg-cfo-blue-300 tw-text-cfo-blue-700 tw-rounded-full tw-w-[32px] tw-h-[32px] tw-font-bold tw-cursor-pointer tw-border-2 tw-border-white"
              onClick={() => setShowDropdown((prev) => !prev)}
              tabIndex={0}
            >
              +{remaining}
            </div>
            {showDropdown &&
              ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  className="tw-bg-white tw-border tw-rounded-[5px] tw-shadow-lg tw-z-9999999 tw-p-2 tw-min-w-[220px] tw-flex tw-flex-col tw-gap-y-[10px] tw-max-h-[170px] tw-overflow-y-auto thin-scrollbar"
                  style={{
                    position: 'fixed',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    zIndex: 9999999,
                  }}
                >
                  {displayUsers?.concat(remainingUsers)?.map((user, idx, arr) => (
                    <div
                      key={`dropdownUser_${user.id}_${idx}`}
                      className={[
                        'tw-flex tw-items-center tw-gap-2 tw-px-2 tw-cursor-default hover:tw-bg-cfo-blue-50 tw-rounded tw-py-0',
                        idx === 0 ? 'tw-pt-[10px]' : '',
                        idx === arr.length - 1 ? 'tw-pb-[10px]' : '',
                      ].join(' ')}
                    >
                      <div className="tw-flex tw-items-center">
                        <div className="tw-relative">
                          <UserAvatar
                            id={`dropdownUser_${user.id}_${idx}`}
                            avatar={user.avatar}
                            name={`${user.firstName || ''} ${user.lastName || ''}`}
                            position={user.position || ''}
                          />
                        </div>
                      </div>
                      <div className="tw-flex tw-flex-col tw-min-w-0">
                        <span className="tw-font-medium tw-truncate tw-max-w-[150px]">
                          {`${user.firstName || ''} ${user.lastName || ''}`}
                        </span>
                        {user.position && (
                          <span
                            style={{ fontSize: '12px' }}
                            className="tw-text-xs tw-text-[12px] tw-text-gray-500 tw-truncate tw-max-w-[150px]"
                          >
                            {user.position}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>,
                document.body
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarGroup;
